import React, { FC, useContext, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { InfoMessage } from '@eika/attention-message';
import { ExpandableInput } from '@eika/expandable-input';
import { IconTypes } from '@eika/icon';

import { AppContext } from '../../containers/appContextContainer';
import {
    EiendomType,
    KontaktinfoWeb,
    SendSoeknadWeb,
    SoeknadStatusWeb,
    SoeknadstypeWeb,
    ValgtWeb,
} from '../../generated-types';
import { useGetBankkonfigurasjon } from '../../hooks/useLaantaker';
import { useSendSoeknad, useSoeknadOppsummering } from '../../hooks/useSoeknadMedlaantaker';
import { useTranslation } from '../../translations';
import DinEpostViewEdit, { DinEpostFormData } from '../kontaktinformasjon/dinEpostViewEdit';
import DinTlfViewEdit, { DinTlfFormData } from '../kontaktinformasjon/dinTlfViewEdit';
import MedlaantakerEpostViewEdit, { MedlaantakerEpostFormData } from '../kontaktinformasjon/medlaantakerEpostViewEdit';
import MedlaantakerTlfViewEdit, { MedlaantakerTlfFormData } from '../kontaktinformasjon/medlaantakerTlfViewEdit';
import UserMessageSoeknadSendtInn from '../modal/userMessageSoeknadSendtInn';
import PageFooter, { ByttonType } from '../page/pageFooter';
import OppsummeringHeader from './oppsummeringHeader';

export type OppsummeringFormData = DinEpostFormData &
    DinTlfFormData &
    MedlaantakerEpostFormData &
    MedlaantakerTlfFormData & {
        notat: string;
    };

interface Props {
    navigateTo: string;
    navigateFrom: string;
}

const Oppsummering: FC<Props> = ({ navigateTo, navigateFrom }) => {
    const navigate = useNavigate();
    const { oppsummering, button } = useTranslation();
    const { data: bankConfig } = useGetBankkonfigurasjon();
    const { soeknadstype } = useContext(AppContext);
    const { data: soeknadContainer } = useSoeknadOppsummering(soeknadstype);
    const soeknad = soeknadContainer?.soeknad;
    const hovedlaantaker = soeknadContainer?.laantaker.find((l) => l.erHovedlaantaker);
    const medlaantaker = soeknadContainer?.laantaker.find((l) => !l.erHovedlaantaker);
    const methods = useForm<OppsummeringFormData>();
    const { register, reset, handleSubmit } = methods;
    const verdivurderingBolig = soeknadContainer?.eiendom.find(
        (it) =>
            (it.type === EiendomType.SELLING &&
                [SoeknadstypeWeb.FINANSIERINGSBEVIS, SoeknadstypeWeb.KJOEPE_BOLIG].includes(soeknadstype)) ||
            (it.type !== EiendomType.BUYING &&
                [
                    SoeknadstypeWeb.FLYTTE_BOLIGLAAN,
                    SoeknadstypeWeb.NYTT_BOLIGLAAN,
                    SoeknadstypeWeb.REFINANSIERE_BOLIGLAAN,
                ].includes(soeknadstype)),
    );
    const { mutate, isPending: isSendingSoeknad } = useSendSoeknad(() => {
        navigate(`${navigateTo}/${soeknad?.soeknadId}`, {
            state:
                soeknad && verdivurderingBolig?.oenskerVerdivurdering === ValgtWeb.JA
                    ? {
                          verdivurderingInfo: {
                              soeknadId: soeknad.soeknadId,
                              adresse: verdivurderingBolig.adresse || '',
                              banknavn: bankConfig?.banknavn || '',
                              etternavn: hovedlaantaker?.etternavn || null,
                              fornavn: hovedlaantaker?.fornavn || null,
                              mail: hovedlaantaker?.epost?.value || null,
                              postnummer: verdivurderingBolig.postnummer || null,
                              poststed: verdivurderingBolig.poststed,
                              tlf: hovedlaantaker?.tlf?.value || null,
                          },
                      }
                    : undefined,
        });
    });

    useEffect((): void => {
        if (hovedlaantaker && medlaantaker) {
            reset({
                notat: (medlaantaker.loggedIn ? medlaantaker.notat : hovedlaantaker.notat) || undefined,
                dinEpost:
                    (medlaantaker.loggedIn ? medlaantaker?.epost?.value : hovedlaantaker?.epost?.value) || undefined,
                dinTlf: (medlaantaker.loggedIn ? medlaantaker?.tlf?.value : hovedlaantaker?.tlf?.value) || undefined,
                medlaantakerEpost: (!medlaantaker.loggedIn && medlaantaker?.epost?.value) || undefined,
                medlaantakerTlf: (!medlaantaker.loggedIn && medlaantaker?.tlf?.value) || undefined,
            });
        } else if (hovedlaantaker) {
            reset({
                notat: '',
                dinEpost: hovedlaantaker?.epost?.value || undefined,
                dinTlf: hovedlaantaker?.tlf?.value || undefined,
            });
        }
    }, [hovedlaantaker, medlaantaker, reset]);

    const onSubmitOppsummering = (data: OppsummeringFormData) => {
        if (soeknad) {
            const kontaktinfo: KontaktinfoWeb[] = [];
            if (hovedlaantaker && hovedlaantaker.loggedIn) {
                kontaktinfo.push({
                    fnr: hovedlaantaker.fnr,
                    epost: {
                        value: data.dinEpost || '',
                    },
                    tlf: { value: data.dinTlf || '' },
                });
            }
            if (medlaantaker) {
                kontaktinfo.push({
                    fnr: medlaantaker.fnr,
                    epost: medlaantaker.loggedIn
                        ? { value: data.dinEpost || '' }
                        : { value: data.medlaantakerEpost || '' },
                    tlf: medlaantaker.loggedIn ? { value: data.dinTlf || '' } : { value: data.medlaantakerTlf || '' },
                });
            }
            const sendSoeknad: SendSoeknadWeb = {
                soeknadId: soeknad.soeknadId,
                status:
                    medlaantaker && hovedlaantaker?.loggedIn
                        ? SoeknadStatusWeb.STEG_MEDLAANTAKER_SAMTYKKER
                        : SoeknadStatusWeb.KLAR_FOR_OVERFOERING_KREDITTPORTALEN,
                notat: data.notat,
                soeknadUrl: `${window.location.origin}`,
                kontaktinfo,
            };
            mutate(sendSoeknad);
        }
    };

    if (soeknad?.status === SoeknadStatusWeb.KLAR_FOR_OVERFOERING_KREDITTPORTALEN)
        return <UserMessageSoeknadSendtInn />;

    return (
        <div>
            <OppsummeringHeader soeknadContainer={soeknadContainer} />
            <div className="sgw-layout medium-centered-container">
                <div className="sgw-layout-item">
                    <FormProvider {...methods}>
                        <form onSubmit={handleSubmit(onSubmitOppsummering)} className="row row-gap-large">
                            <h2 className="margin-desktop-top-larger">{oppsummering.subTitleKontaktinfo}</h2>
                            <div className="form-column">
                                <div className="sgw-layout">
                                    <div className="sgw-layout-item row row-gap-large">
                                        <DinEpostViewEdit />
                                        <DinTlfViewEdit />
                                    </div>
                                    {medlaantaker && !medlaantaker.loggedIn && (
                                        <div className="sgw-layout-item row row-gap-large">
                                            <MedlaantakerEpostViewEdit />
                                            <MedlaantakerTlfViewEdit />
                                        </div>
                                    )}
                                </div>
                            </div>
                            {medlaantaker && !medlaantaker.loggedIn && (
                                <InfoMessage
                                    text={oppsummering.infoSms.replace(
                                        '{navn}',
                                        `${medlaantaker.fornavn} ${medlaantaker.etternavn}`,
                                    )}
                                    icon={IconTypes.INFO_36}
                                    type="default"
                                />
                            )}
                            <h3>{oppsummering.subTitleBeskjedRaadgiver}</h3>
                            <div>
                                <label className="sgw-input__input-mainlabel">{oppsummering.labelBeskjed}</label>
                                <ExpandableInput {...register('notat')} />
                            </div>
                            <p>{oppsummering.infoSend}</p>
                            <PageFooter
                                soeknadId={soeknad?.soeknadId}
                                showSpinner={isSendingSoeknad}
                                buttonText={button.sendSoeknad}
                                buttonType={ByttonType.SUBMIT}
                                onForrigeClick={() => navigate(navigateFrom)}
                            />
                        </form>
                    </FormProvider>
                </div>
            </div>
        </div>
    );
};

export default Oppsummering;
